// Define the columns for the TableData component

import { Link } from "react-router-dom";

import { Column, ColumnDef } from "@tanstack/react-table";

import { ArrowSortIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { COUNTRY_NAME } from "@/constants/dataset";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import { DataSchemaType, DatasetExploreItem } from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";
import { formatDate } from "@/utils/date";

import { MoreVertical } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import AggregationBadge from "./AggregationBadge";
import BadgeStatus from "./BadgeStatus";

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
>;
function SortedHeader({
  column,
  title
}: {
  column: Column<DatasetExploreItem>;
  title: string;
}) {
  return (
    <Button
      variant="ghost"
      className="flex flex-row items-center p-0 h-0"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    >
      <p className="text-txtBody text-xs font-semibold">{title}</p>
      <ArrowSortIcon width={16} height={16} />
    </Button>
  );
}

export const columns = (timezone?: {
  code: string;
  name: string;
}): ExtendedColumnDef<DatasetExploreItem>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <SortedHeader column={column} title="Dataset Title" />
    ),
    cell: ({ getValue, row }) => (
      <>
        <p className="text-txtBody text-xs font-semibold truncate">
          {getValue() as string}
        </p>
        <p className="text-txtBody text-xs line-clamp-2">
          {row.original.short_description}
        </p>
      </>
    ),
    enableSorting: true
  },
  {
    accessorKey: "country_id",
    header: ({ column }) => <SortedHeader column={column} title="Country" />,
    enableSorting: true,
    cell: (info) => (
      <div>{COUNTRY_NAME[info.getValue() as CountryCode | "all"]}</div>
    )
  },
  {
    accessorKey: "spatial_aggregation_type",
    header: ({ column }) => (
      <SortedHeader column={column} title="Spatial Aggregation" />
    ),
    cell: (info) => (
      <AggregationBadge
        value={
          info.getValue() as Exclude<
            SpatialAggregationType,
            EOutputAnalysisType
          >
        }
      />
    ),
    enableSorting: true
  },
  {
    accessorKey: "published",
    header: ({ column }) => <SortedHeader column={column} title="Status" />,
    cell: (info) => <BadgeStatus value={info.getValue() as boolean} />,
    enableSorting: true
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortedHeader column={column} title="Date Added" />,
    cell: (info) => (
      <p className="text-txtBody text-xs">
        {formatDate(
          info.getValue() as string,
          DataSchemaType.DATE_TIME,
          timezone
        )}
      </p>
    ),
    enableSorting: true
  },
  {
    accessorKey: "id",
    header: () => <p className="text-txtBody text-xs font-semibold">Action</p>,
    cell: (data) => (
      <div className="flex justify-center">
        <Popover>
          <PopoverTrigger asChild>
            <button className="p-2 rounded-full hover:bg-gray-100">
              <MoreVertical size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="px-3 py-1 w-auto" side="left">
            <Link
              to={`/dataset-management/${data.row.original.id}`}
              className="text-xs"
              // TODO: will be replaced with a new type baseon real data when detail page is already integrate API
              state={(data.row.index % 3) + 1}
            >
              View Dataset
            </Link>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
];
